import { Component, Signal, inject, signal, computed, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { PanelModule } from 'primeng/panel';
import { DividerModule } from 'primeng/divider';
import { DropdownModule } from 'primeng/dropdown';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { AssetsState } from '../assets/store/assets.state';
import * as AssetsActions from '../assets/store/assets.actions';
import * as AssetsSelectors from '../assets/store/assets.selector';
import { BaseListComponent } from '../shared/components/base-list.component';
import { ColumnFilters, TableColumn } from '../shared/models/ui/table-column.model';
import { MaintenanceAsset } from '../shared/models/operation/maintenance-asset.model';
import { CommonService } from '../shared/services/common.service';
import { AssetsService } from '../shared/services/assets.service';
import { catchError, takeUntil } from 'rxjs/operators';
import { of } from 'rxjs';
import { MaintenanceAssetFormDialogComponent } from '../assets/maintenance-assets/maintenance-asset-form-dialog/maintenance-asset-form-dialog.component';
import { environment } from '../../environments/environment';
import { ConfirmationService, MessageService } from 'primeng/api';

interface AssetTypeOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-maintenance-records-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    PanelModule,
    DividerModule,
    DropdownModule,
    ToastModule,
    ConfirmDialogModule,
    MaintenanceAssetFormDialogComponent
  ],
  templateUrl: './maintenance-records-list.component.html',
  styleUrl: './maintenance-records-list.component.scss',
  providers: [ConfirmationService, MessageService]
})
export class MaintenanceRecordsListComponent extends BaseListComponent implements OnInit, OnDestroy {
  private store = inject(Store<AssetsState>);
  private actions$ = inject(Actions);
  private commonService = inject(CommonService);
  private assetsService = inject(AssetsService);
  private messageService = inject(MessageService);

  data: Signal<any[]> = toSignal(this.store.select(AssetsSelectors.selectMaintenanceAssetsData), { initialValue: [] });
  totalRecords: Signal<number> = toSignal(this.store.select(AssetsSelectors.selectMaintenanceAssetsTotal), { initialValue: 0 });
  loading: Signal<boolean> = toSignal(this.store.select(AssetsSelectors.selectAssetsLoading), { initialValue: false });

  selectedAssetType = signal('bollards');
  selectedRecords = signal<MaintenanceAsset[]>([]);

  item: MaintenanceAsset = {} as MaintenanceAsset;
  isDialogReadOnly = signal(true);
  pendingFile = signal<File | null>(null);

  // Bulk delete dispatches one action per record; tally outcomes so a single toast is shown.
  private pendingDeletes = 0;
  private deletedCount = 0;
  private failedDeleteCount = 0;

  deleteButtonLabel = computed(() => {
    return this.selectedRecords().length > 1 ? 'Delete Multiple' : 'Delete';
  });

  columns: TableColumn[] = [
    { field: 'asset_code', header: 'Asset Code' },
    { field: 'grp_no', header: 'GRP No.' },
    { field: 'maintenance_date', header: 'Maintenance Date', type: 'date', filterable: false },
    { field: 'maintenance_documentation', header: 'Maintenance Documentation', filterable: false },
    { field: 'next_maintenance_date', header: 'Next Maintenance Date', type: 'date', filterable: false },
    { field: 'maintenance_details', header: 'Maintenance Details', filterable: false },
    { field: 'maintenance_type', header: 'Maintenance Type' },
    { field: 'quantity', header: 'Quantity', type: 'number', filterable: false },
    { field: 'rate', header: 'Rate', type: 'number', filterable: false },
    { field: 'total_cost', header: 'Total Cost', type: 'number', filterable: false },
    { field: 'remarks', header: 'Remarks' },
  ];

  assetTypeOptions: AssetTypeOption[] = [
    { label: 'Bollard', value: 'bollards' },
    { label: 'Bridge', value: 'bridges' },
    { label: 'Bus Stop', value: 'bus_stops' },
    { label: 'Fence', value: 'fences' },
    { label: 'GeoTech Structure', value: 'geo_tech_structures' },
    { label: 'Guard Rail', value: 'guardrails' },
    { label: 'Multipurpose Hall', value: 'multipurpose_halls' },
    { label: 'Parking', value: 'parking' },
    { label: 'Pavement', value: 'pavements' },
    { label: 'Public Toilet', value: 'public_toilets' },
    { label: 'Road Kerb', value: 'road_kerbs' },
    { label: 'Road Marking', value: 'road_markings' },
    { label: 'Traffic Light', value: 'traffic_lights' },
    { label: 'Traffic Sign', value: 'traffic_signs' },
    { label: 'Walkway', value: 'walkways' },
    { label: 'Bin Centre', value: 'bin_centres' },
    { label: 'Drainage', value: 'drainages' },
    { label: 'Drainage Cover', value: 'drainage_covers' },
  ];

  lccaMap = signal<Map<number, string>>(new Map());
  maintenanceTypeMap = signal<Map<number, string>>(new Map());

  get hasActiveFilters(): boolean {
    return Object.values(this.columnFilters || {}).some(v => !!v?.trim());
  }

  override ngOnInit(): void {
    const assetType = this.route.snapshot.queryParams['asset_type'];
    if (assetType && this.assetTypeOptions.some(option => option.value === assetType)) {
      this.selectedAssetType.set(assetType);
    }

    this.loadLookupData();
    super.ngOnInit();

    this.actions$.pipe(
      ofType(AssetsActions.deleteMaintenanceAssetSuccess),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.deletedCount++;
      this.settleDelete();
    });

    this.actions$.pipe(
      ofType(AssetsActions.deleteMaintenanceAssetFailure),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.failedDeleteCount++;
      this.settleDelete();
    });

    this.actions$.pipe(
      ofType(AssetsActions.createMaintenanceAssetSuccess, AssetsActions.updateMaintenanceAssetSuccess),
      takeUntil(this.destroy$)
    ).subscribe((action: { item?: MaintenanceAsset }) => {
      const maintenanceAssetId = action.item?.id || this.item?.id;
      const fileToUpload = this.pendingFile();

      if (fileToUpload && maintenanceAssetId) {
        this.uploadDocumentationFile(maintenanceAssetId, fileToUpload);
      } else {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Maintenance record saved successfully'
        });
        this.selectedRecords.set([]);
        this.loadData(Math.floor(this.first / this.rows) + 1, this.rows, this.columnFilters);
        if (this.itemDialog) {
          this.itemDialog = false;
        }
      }
    });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  override loadData(page: number, size: number, filters: ColumnFilters): void {
    this.store.dispatch(AssetsActions.findMaintenanceAssets({
      assetType: this.selectedAssetType(),
      page,
      size,
      filters
    }));
  }

  protected override updateUrlParams(page: number, size: number, filters: ColumnFilters): void {
    const queryParams: Record<string, string | number> = {
      page,
      size,
      asset_type: this.selectedAssetType(),
      ...filters
    };

    this.persistListCriteria(page, size, filters);

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      replaceUrl: true
    });
  }

  onAssetTypeChange(assetType: string): void {
    this.selectedAssetType.set(assetType);
    this.selectedRecords.set([]);
    this.first = 0;
    this.updateUrlParams(1, this.rows, this.columnFilters);
    this.loadData(1, this.rows, this.columnFilters);
  }

  isFilterable(col: TableColumn): boolean {
    if (col.filterable === false) {
      return false;
    }
    if (col.filterable === true) {
      return true;
    }
    return !['image', 'date', 'number', 'boolean'].includes(col.type || '');
  }

  filterValue(field: string): string {
    return this.columnFilters?.[field] || '';
  }

  onColumnFilter(field: string, value: string): void {
    const current = this.columnFilters?.[field] || '';
    const nextValue = value ?? '';
    if (current === nextValue) {
      return;
    }

    const next: ColumnFilters = { ...(this.columnFilters || {}) };
    if (nextValue.trim()) {
      next[field] = nextValue;
    } else {
      delete next[field];
    }
    this.onFilter(next);
  }

  stopRowEvent(event: Event): void {
    event.stopPropagation();
  }

  editRecord(record: MaintenanceAsset, event?: Event): void {
    event?.stopPropagation();
    this.item = { ...record };
    this.isDialogReadOnly.set(false);
    this.pendingFile.set(null);
    this.itemDialog = true;
  }

  onRowDblClick(record: MaintenanceAsset): void {
    this.item = { ...record };
    this.isDialogReadOnly.set(true);
    this.pendingFile.set(null);
    this.itemDialog = true;
  }

  onDialogSave(data: Partial<MaintenanceAsset>): void {
    const itemToSave = { ...this.item, ...data };
    const masterRecId = itemToSave.master_rec_id ?? itemToSave.MASTER_REC_ID;

    if (itemToSave.id) {
      this.store.dispatch(AssetsActions.updateMaintenanceAsset({
        assetType: this.selectedAssetType(),
        id: itemToSave.id,
        item: itemToSave
      }));
      return;
    }

    if (!masterRecId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Please select a parent asset'
      });
      return;
    }

    itemToSave.master_rec_id = masterRecId;
    this.store.dispatch(AssetsActions.createMaintenanceAsset({
      assetType: this.selectedAssetType(),
      item: itemToSave
    }));
  }

  onFileSelected(file: File): void {
    this.pendingFile.set(file);
  }

  deleteSingleRecord(record: MaintenanceAsset, event?: Event): void {
    event?.stopPropagation();
    this.selectedRecords.set([record]);
    this.deleteRecords();
  }

  deleteRecords(): void {
    if (this.selectedRecords().length === 0) {
      return;
    }

    const recordsToDelete = [...this.selectedRecords()];
    const count = recordsToDelete.length;

    let message: string;
    if (count === 1) {
      const code = recordsToDelete[0].asset_code || 'this record';
      message = `Are you sure you want to delete maintenance record for "${code}"?`;
    } else {
      const maxDisplay = 10;
      const codesToShow = recordsToDelete.slice(0, maxDisplay);
      const codesList = codesToShow
        .map((r, index) => `${index + 1}. ${r.asset_code || r.id}`)
        .join('<br>');

      let fullMessage = `Are you sure you want to delete ${count} maintenance records?<br><br><strong>Records to be deleted:</strong><br>${codesList}`;
      if (count > maxDisplay) {
        fullMessage += '<br>etc.';
      }
      message = fullMessage;
    }

    this.confirmationService.confirm({
      message,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        const ids = recordsToDelete
          .map(record => record.id)
          .filter((id): id is number => !!id);

        if (ids.length === 0) {
          return;
        }

        this.pendingDeletes = ids.length;
        this.deletedCount = 0;
        this.failedDeleteCount = 0;

        ids.forEach(id => {
          this.store.dispatch(AssetsActions.deleteMaintenanceAsset({
            assetType: this.selectedAssetType(),
            id
          }));
        });
      }
    });
  }

  private settleDelete(): void {
    if (this.pendingDeletes > 0) {
      this.pendingDeletes--;
    }

    if (this.pendingDeletes > 0) {
      return;
    }

    const deleted = this.deletedCount;
    const failed = this.failedDeleteCount;
    this.deletedCount = 0;
    this.failedDeleteCount = 0;

    if (deleted > 0) {
      this.messageService.add({
        severity: 'success',
        summary: 'Deleted',
        detail: deleted === 1
          ? 'Maintenance record deleted successfully'
          : `${deleted} maintenance records deleted successfully`
      });
    }

    if (failed > 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: failed === 1
          ? 'Failed to delete 1 maintenance record'
          : `Failed to delete ${failed} maintenance records`
      });
    }

    this.selectedRecords.set([]);
    this.loadData(Math.floor(this.first / this.rows) + 1, this.rows, this.columnFilters);
  }

  refreshRecords(): void {
    this.selectedRecords.set([]);
    this.clearFilter();
    this.messageService.add({
      severity: 'info',
      summary: 'Refreshed',
      detail: 'Maintenance records list has been refreshed'
    });
  }

  getMaintenanceDetailsName(id: number | undefined): string {
    if (!id) return '';
    return this.lccaMap().get(id) || String(id);
  }

  getMaintenanceTypeName(id: number | string | undefined): string {
    if (!id) return '';
    const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
    if (isNaN(numericId)) return String(id);
    return this.maintenanceTypeMap().get(numericId) || String(id);
  }

  getFileName(filePath: string | null | undefined): string {
    if (!filePath) return '';
    return filePath.split('/').pop() || filePath;
  }

  getFileUrl(filePath: string | null | undefined): string {
    if (!filePath) return '';
    if (filePath.startsWith('http://') || filePath.startsWith('https://') || filePath.startsWith('data:')) {
      return filePath;
    }
    return `${environment.mediaHubUrl}/${filePath}`;
  }

  private uploadDocumentationFile(maintenanceAssetId: number, file: File): void {
    this.assetsService.uploadAssetFile('maintenance_' + this.selectedAssetType(), maintenanceAssetId, file)
      .pipe(
        takeUntil(this.destroy$),
        catchError((error) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: error?.error?.detail || 'Failed to upload documentation file'
          });
          this.pendingFile.set(null);
          this.selectedRecords.set([]);
          this.loadData(Math.floor(this.first / this.rows) + 1, this.rows, this.columnFilters);
          if (this.itemDialog) {
            this.itemDialog = false;
          }
          return of(null);
        })
      )
      .subscribe((response) => {
        if (response) {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Maintenance record saved successfully'
          });
        }
        this.pendingFile.set(null);
        this.selectedRecords.set([]);
        this.loadData(Math.floor(this.first / this.rows) + 1, this.rows, this.columnFilters);
        if (this.itemDialog) {
          this.itemDialog = false;
        }
      });
  }

  private loadLookupData(): void {
    this.commonService.findLccas(1, 500, '').pipe(takeUntil(this.destroy$)).subscribe(result => {
      const map = new Map<number, string>();
      (result.data || []).forEach((item: { id?: number; ID?: number; maintenance_detail?: string; lcca_code?: string }) => {
        const id = item.id || item.ID;
        if (id) map.set(id, item.maintenance_detail || item.lcca_code || '');
      });
      this.lccaMap.set(map);
    });

    this.commonService.findLccaTypes(1, 500, '').pipe(takeUntil(this.destroy$)).subscribe(result => {
      const map = new Map<number, string>();
      (result.data || []).forEach((item: { id?: number; ID?: number; name?: string }) => {
        const id = item.id || item.ID;
        if (id) map.set(id, item.name || '');
      });
      this.maintenanceTypeMap.set(map);
    });
  }
}
